import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ScrollText } from "lucide-react";

const ACTION_TONE = {
  "auth.login": "text-teal-700",
  "auth.logout": "text-slate-500",
  "dossier.export": "text-purple-700",
  "user.create": "text-blue-700",
  "user.update": "text-blue-700",
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit", { params: filter ? { action: filter } : {} });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="audit-log-page">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-purple-700 flex items-center gap-1.5"><ScrollText className="h-4 w-4" /> Administration</div>
        <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Audit log</h1>
        <p className="text-sm text-slate-500 mt-1">Every sensitive action — logins, views, searches, exports, admin changes — is recorded.</p>
      </header>

      <Input data-testid="audit-filter" placeholder="Filter by action (e.g. dossier.export, ads.view, auth.login)…"
        value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-md" />

      <section className="border border-slate-200 bg-white rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="audit-empty">No matching audit entries.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Time (UTC)</th>
                <th className="text-left px-4 py-2.5">Actor</th>
                <th className="text-left px-4 py-2.5">Action</th>
                <th className="text-left px-4 py-2.5">Target</th>
                <th className="text-left px-4 py-2.5">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-4 py-2 text-xs"><span className="font-medium">{r.actor_name || "—"}</span> <span className="text-slate-400 font-mono">({r.actor_role})</span></td>
                  <td className={`px-4 py-2 font-mono text-xs font-medium ${ACTION_TONE[r.action] || "text-slate-700"}`}>{r.action}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 font-mono truncate max-w-[220px]">{r.entity_type}{r.entity_id ? ` · ${r.entity_id.slice(0, 12)}` : ""}{r.detail ? ` · ${r.detail}` : ""}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-400">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
